import { useEffect, useState } from 'react';
import {
  getCachedUser,
  fetchMe,
  parseAuthCallback,
  clearSession,
  type KQUser,
} from '@/lib/auth';

/**
 * useAuth — call once at the App level (or any component that needs the user).
 * Cheap because the cached user is read sync from localStorage.
 */
export function useAuth() {
  const [user, setUser] = useState<KQUser | null>(() => getCachedUser());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // 1. If we just landed from Google OAuth callback, capture the hash.
    const captured = parseAuthCallback();
    if (captured) setUser(getCachedUser());

    // 2. Validate the session against /me to confirm + refresh profile.
    (async () => {
      setLoading(true);
      const fresh = await fetchMe();
      if (fresh) setUser(fresh);
      else if (!captured) {
        // No fresh data, keep cached. If unauthenticated, ensure null.
        const c = getCachedUser();
        if (!c) setUser(null);
      }
      setLoading(false);
    })();
  }, []);

  function logout() {
    clearSession();
    setUser(null);
  }

  return { user, loading, logout };
}
